import { Stack } from "@chakra-ui/react";
import React, { memo, useCallback } from "react";

import colors from "app/colors";
import { ReplyIcon } from "modules/icons";
import { MessageFragment, MessageThreadFragment } from "gql/graphql";
import MessagePopoverQuickReplyButton from "./message-popover-quick-reply-button";
import useMessageThreadDraft from "modules/hooks/use-message-thread-draft";
import useCreateMessage from "modules/hooks/use-create-message";
import { usePubSub } from "modules/providers/pub-sub-provider";

const QUICK_REPLIES = [
  "On it!",
  "Thanks!",
  "What is the status?",
  "I'll get back to you.",
  // "Reply to message", // This is added manually at the end of the list with a different onClick handler
];

interface IProps {
  messageThread: MessageThreadFragment;
  message: MessageFragment;
}

const MessagePopoverQuickRepliesButtons: React.FC<IProps> = ({
  messageThread,
  message,
}) => {
  const { publish } = usePubSub();
  const { setQuotedMessages } = useMessageThreadDraft({
    draftId: messageThread.id,
  });

  const { createMessage, getQuickReplyMessageInput, getQuotedMessage } =
    useCreateMessage(message.messageThreadId);

  const onReplyToMessageClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      e.preventDefault();
      publish("focus-quill-editor", message.messageThreadId);
      setQuotedMessages(message);
    },
    [message, publish, setQuotedMessages],
  );

  const onQuickReplyClick = useCallback(
    (quickReply: string) => () => {
      const messageInput = getQuickReplyMessageInput(quickReply, message.id);
      const quotedMessage = getQuotedMessage(message);
      createMessage({
        messageInput,
        quotedMessages: [quotedMessage],
      });
    },
    [createMessage, getQuickReplyMessageInput, getQuotedMessage, message],
  );

  return (
    <Stack w="273px" p={2} spacing={0}>
      {QUICK_REPLIES.map((quickReply, index) => (
        <MessagePopoverQuickReplyButton
          key={index}
          onClick={onQuickReplyClick(quickReply)}
        >
          {quickReply}
        </MessagePopoverQuickReplyButton>
      ))}
      <MessagePopoverQuickReplyButton
        onClick={onReplyToMessageClick}
        color={colors.blue}
        leftIcon={<ReplyIcon fill={colors.blue} />}
      >
        Reply to message
      </MessagePopoverQuickReplyButton>
    </Stack>
  );
};

export default memo(MessagePopoverQuickRepliesButtons);
